<template>
  <CustomModal ref="modal" class="client-informations-missing-modal" title="Informations client manquantes">
    <div class="missing-content">
      <div class="icon" aria-hidden="true">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 8v5"/><path d="M12 16.5h.01"/><circle cx="12" cy="12" r="9.5"/></svg>
      </div>

      <div class="heading">
        <p class="intro">{{ intro }}</p>
        <p class="sub">La facture pourra être générée dès que sa fiche sera complétée.</p>
      </div>

      <ul class="missing-list">
        <li v-for="item in items" :key="item">
          <span class="bullet" aria-hidden="true"></span>
          <span class="item-text">{{ item }}</span>
        </li>
      </ul>

      <div class="actions">
        <CustomButton class="go-button" :params="goButton" @click="goToClient" />
        <CustomButton :is-secondary="true" :params="buttons.close" @click="closeModal" />
      </div>
    </div>
  </CustomModal>
</template>

<script>
import router from "@/router";
import CustomModal from "@/components/modals/CustomModal.vue";
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";

// Code d'erreur renvoyé par ms-business quand c'est l'adresse du client qui manque ou est incomplète
const CLIENT_ADDRESS_MISSING = "CLIENT_ADDRESS_MISSING";

/**
 * Fenêtre affichée quand un document ne peut pas être généré parce que la fiche du client est incomplète :
 * elle dit ce qui manque et mène directement à la fiche du client, ouverte sur la section à compléter.
 */
export default {
  name: 'ClientInformationsMissingModal',
  components: {CustomModal, CustomButton},
  props: {
    // Appelée avant de quitter la page : ferme la fenêtre qui a déclenché l'erreur
    callback: {type: Function, default: undefined},
  },
  data() {
    return {
      clientId: undefined,
      code: undefined,
      message: '',
      buttons: {
        close: {text: "Fermer"},
      },
    };
  },
  computed: {
    isAddressMissing() {
      return this.code === CLIENT_ADDRESS_MISSING;
    },
    intro() {
      return this.isAddressMissing
          ? "Votre client n'a pas d'adresse complète."
          : "Il manque des informations sur votre client.";
    },
    goButton() {
      return {
        text: this.isAddressMissing
            ? "Se rendre au client pour renseigner l'adresse"
            : "Se rendre au client pour compléter ses informations"
      };
    },
    // Le message du serveur est de la forme « Impossible de … : élément ; élément. À compléter … » : on liste les éléments
    items() {
      const separator = this.message.indexOf(' : ');
      if (separator === -1) return [this.message];
      const list = this.message.slice(separator + 3).split('. À compléter')[0].replace(/\.$/, '');
      const items = list.split(' ; ').map(item => item.trim()).filter(Boolean);
      return items.map(item => item.charAt(0).toUpperCase() + item.slice(1));
    },
  },
  methods: {
    // error : l'erreur renvoyée par le serveur ({code, message})
    openModal(clientId, error) {
      this.clientId = clientId;
      this.code = error.code;
      this.message = error.message;
      this.$refs.modal.openModal();
    },
    closeModal() {
      this.$refs.modal?.closeModal();
    },
    goToClient() {
      this.closeModal();
      if (this.callback) this.callback();
      router.push({
        path: '/clients',
        query: {clientId: this.clientId, section: this.isAddressMissing ? 'address' : 'identity'},
        hash: '#open-client'
      });
    },
  }
}
</script>

<style lang="stylus">
.client-informations-missing-modal .modal
  width 640px !important
  max-width 94vw !important

// Le titre tient sur une seule ligne dès que la fenêtre est assez large
@media only screen and (min-width: 700px)
  .client-informations-missing-modal .modal h2
    white-space nowrap
</style>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.missing-content
  display flex
  flex-direction column
  align-items center
  gap 18px
  padding 4px 32px 14px

  p
    margin 0

.icon
  display flex
  align-items center
  justify-content center
  width 56px
  height 56px
  border-radius 50%
  background-color #e3eefc
  color #0069de

.heading
  text-align center

  .intro
    font-size 1.1em
    font-weight 700
    color FONT_COLOR

  .sub
    margin-top 4px
    font-size 0.92em
    color #5d6b82

.missing-list
  list-style none
  width 100%
  margin 0
  padding 0
  display flex
  flex-direction column
  gap 8px

  li
    display flex
    align-items flex-start
    gap 10px
    padding 10px 12px
    border 1px solid #c5dbf6
    border-left 4px solid #0069de
    border-radius 8px
    background-color #f4f8fe
    color FONT_COLOR
    line-height 1.4

  .bullet
    flex 0 0 auto
    width 6px
    height 6px
    margin-top 7px
    border-radius 50%
    background-color #0069de

  .item-text
    min-width 0
    word-break break-word

.actions
  display flex
  flex-direction column
  gap 10px
  width 100%
  margin-top 4px

  .go-button
    width 100%
</style>
